import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { api, formatApiError } from "../../api";
import { CONTROL } from "../../constants/testIds";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { toast } from "../ui/sonner";

// DEFECT-D-06/D-07: a genuine "block every login for this gym" suspend, plus a soft delete —
// both distinct from `SuspendRestorePanel`'s billing read-only hold above it on the same tab
// (that one deliberately still lets everyone log in; this one deliberately does not). The delete
// dialog now shows exactly what it will affect (member/staff/branch/payment counts) before the
// operator can even start typing the confirmation, blocks on an active paid subscription unless
// explicitly overridden, and a deleted gym can be fully restored within its recovery window.
export function GymSuspendPanel({ orgId, displayName, status, suspension, canEdit }) {
  const queryClient = useQueryClient();
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [forceDelete, setForceDelete] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["control-gym-detail", orgId] });
    queryClient.invalidateQueries({ queryKey: ["control-gyms"] });
    queryClient.invalidateQueries({ queryKey: ["platform-summary"] });
  };

  const previewQ = useQuery({
    queryKey: ["gym-deletion-preview", orgId],
    queryFn: async () => (await api.get(`/control/gyms/${orgId}/deletion-preview`)).data,
    enabled: deleteOpen,
  });

  const suspendMutation = useMutation({
    mutationFn: () => api.post(`/control/gyms/${orgId}/suspend`, { reason }),
    onSuccess: () => { toast.success(`${displayName} suspended — every login is now blocked.`); setSuspendOpen(false); setReason(""); invalidate(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const unsuspendMutation = useMutation({
    mutationFn: () => api.post(`/control/gyms/${orgId}/unsuspend`),
    onSuccess: () => { toast.success(`${displayName} reinstated.`); invalidate(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.post(`/control/gyms/${orgId}/delete`, null, { params: forceDelete ? { force: true } : {} }),
    onSuccess: (res) => { toast.success(`${displayName} deleted. It can be restored within ${res.data.recovery_window_days} days.`); setDeleteOpen(false); invalidate(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const restoreMutation = useMutation({
    mutationFn: () => api.post(`/control/gyms/${orgId}/restore`),
    onSuccess: () => { toast.success(`${displayName} restored.`); invalidate(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (status === "deleted") {
    return (
      <div className="solid-surface rounded-[14px] p-5 border space-y-3" style={{ borderColor: "var(--scarlet-neg)" }}>
        <div>
          <p className="text-sm font-semibold" style={{ color: "var(--scarlet-neg)" }}>This gym has been deleted.</p>
          {suspension?.reason && <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>{suspension.reason}</p>}
        </div>
        <Button data-testid={CONTROL.gymRestoreButton} disabled={!canEdit || restoreMutation.isPending} onClick={() => restoreMutation.mutate()} className="gap-1.5 text-white" style={{ background: "var(--scarlet-pos)" }}>
          <RotateCcw size={14} /> Restore gym
        </Button>
      </div>
    );
  }

  return (
    <div className="solid-surface rounded-[14px] p-5 space-y-3 border" style={{ borderColor: "rgba(214, 32, 43,0.3)" }}>
      <div className="flex items-center gap-2">
        <AlertTriangle size={16} style={{ color: "var(--scarlet-neg)" }} />
        <h3 className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Suspend gym (blocks every login)</h3>
      </div>
      <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
        {status === "suspended"
          ? `Suspended${suspension?.reason ? `: "${suspension.reason}"` : ""}. Owner, staff and members cannot log in until reinstated.`
          : "A trust & safety action — unlike a billing hold, this blocks login entirely for the owner, every staff member and every member of this gym. Reversible anytime."}
      </p>
      {status === "suspended" ? (
        <Button data-testid={CONTROL.gymUnsuspendButton} disabled={!canEdit || unsuspendMutation.isPending} onClick={() => unsuspendMutation.mutate()}>
          Reinstate gym
        </Button>
      ) : (
        <Button data-testid={CONTROL.gymSuspendButton} disabled={!canEdit} onClick={() => setSuspendOpen(true)} className="text-white" style={{ background: "var(--scarlet-neg)" }}>
          Suspend gym
        </Button>
      )}

      <div className="border-t pt-3" style={{ borderColor: "var(--scarlet-border)" }}>
        <h3 className="text-sm font-semibold mb-1.5" style={{ color: "var(--scarlet-text)" }}>Delete gym</h3>
        <p className="text-sm mb-2" style={{ color: "var(--scarlet-text-secondary)" }}>
          Blocks login and revokes the license. Financial/audit records are retained for compliance, never erased — and it can be fully restored within the recovery window.
        </p>
        <Button data-testid={CONTROL.gymDeleteButton} disabled={!canEdit} variant="outline" onClick={() => { setForceDelete(false); setDeleteOpen(true); }} style={{ color: "var(--scarlet-neg)", borderColor: "var(--scarlet-neg)" }}>
          Delete gym
        </Button>
      </div>

      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent data-testid={CONTROL.gymSuspendDialog} className="max-w-md">
          <DialogHeader><DialogTitle>Suspend {displayName}?</DialogTitle></DialogHeader>
          <Textarea data-testid={CONTROL.gymSuspendReasonInput} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" rows={3} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <Button data-testid={CONTROL.gymSuspendConfirmButton} disabled={!reason.trim() || suspendMutation.isPending} onClick={() => suspendMutation.mutate()} className="text-white" style={{ background: "var(--scarlet-neg)" }}>
              Suspend
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={(v) => { setDeleteOpen(v); if (!v) { setDeleteConfirmText(""); setForceDelete(false); } }}>
        <DialogContent data-testid={CONTROL.gymDeleteDialog} className="max-w-md">
          <DialogHeader><DialogTitle>Delete {displayName}?</DialogTitle></DialogHeader>
          {previewQ.isLoading ? (
            <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>Checking what this affects…</p>
          ) : previewQ.data && (
            <div data-testid={CONTROL.gymDeletePreview} className="rounded-[10px] p-3 text-sm space-y-1" style={{ background: "rgba(214, 32, 43,0.06)" }}>
              <p style={{ color: "var(--scarlet-text)" }}>This will affect:</p>
              <ul className="list-disc pl-5" style={{ color: "var(--scarlet-text-secondary)" }}>
                <li>{previewQ.data.counts.members} member(s)</li>
                <li>{previewQ.data.counts.staff_accounts} staff account(s)</li>
                <li>{previewQ.data.counts.branches} branch(es)</li>
                <li>{previewQ.data.counts.payment_records} payment record(s) — retained, never erased</li>
              </ul>
              {previewQ.data.has_active_paid_subscription && (
                <p className="font-semibold pt-1" style={{ color: "var(--scarlet-neg)" }}>
                  This gym has an active paid subscription. Deleting it will require an explicit override below.
                </p>
              )}
            </div>
          )}
          <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
            Type <strong>{displayName}</strong> to confirm. This can be undone within {previewQ.data?.recovery_window_days ?? 30} days via "Restore gym".
          </p>
          <Input data-testid={CONTROL.gymDeleteConfirmInput} value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder={displayName} />
          {previewQ.data?.has_active_paid_subscription && (
            <label className="flex items-center gap-2 text-sm" style={{ color: "var(--scarlet-text)" }}>
              <input data-testid={CONTROL.gymDeleteForceCheckbox} type="checkbox" checked={forceDelete} onChange={(e) => setForceDelete(e.target.checked)} />
              I understand this gym has an active paid subscription — delete anyway.
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              data-testid={CONTROL.gymDeleteConfirmButton}
              disabled={deleteConfirmText !== displayName || deleteMutation.isPending || (previewQ.data?.has_active_paid_subscription && !forceDelete)}
              onClick={() => deleteMutation.mutate()}
              className="text-white" style={{ background: "var(--scarlet-neg)" }}
            >
              Delete gym
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
